import React, { useCallback, useMemo } from "react";

import { Spacer } from "shared/layout";
import {
  Card,
  ICardTitle,
  RTEContent,
  InfoBlockItems,
} from "shared/components";
import { capitalizeFirstLetter } from "shared/utils";
import { useStagePageContext, useVenuePageContext } from "Venues/contexts";
import {
  getInfoBlockActions,
  getInfoBlockItem,
  useVenueRoute,
} from "Venues/utils";
import { copy } from "shared/assets";
import { IVenueBaseInfoBlock } from "Venues/types";
import { IInfoBlockItem } from "shared/types";
import {
  getInfoBlockItemReadableName,
  useInfoBlockItemsProps,
} from "shared/utils";

interface InfoBlockProps {
  infoBlock: IVenueBaseInfoBlock;
  title: ICardTitle;
  editPath: string;
  extraProps?: { stage?: string; venue?: string };
  icon?: React.ComponentType<any>;
}

export const InfoBlock: React.FC<InfoBlockProps> = ({
  infoBlock,
  icon,
  extraProps,
  editPath,
}) => {
  const { pushRoute } = useVenueRoute();
  const {
    venue,
    userPermissions,
    brandColor,
    openDeleteInfoBlockModal,
    openSuggestEditModal,
  } = useVenuePageContext();
  const { stage, openSuggestStageEditModal } = useStagePageContext();

  const infoBlockType = useMemo(
    () => getInfoBlockItem(infoBlock.type),
    [infoBlock.type]
  );

  const itemsProps = useInfoBlockItemsProps(
    {
      places: infoBlock.places || [],
      contacts: infoBlock.contacts || [],
      files: infoBlock.files || [],
      images: infoBlock.images || [],
    },
    "view"
  );

  const getBreadcrumbs = useCallback(
    (type: IInfoBlockItem) => {
      if (!infoBlockType) return undefined;

      const initBreadcrumb = [
        infoBlockType?.label,
        capitalizeFirstLetter(
          copy.infoBlockItem.list.title.replace(
            "%s",
            getInfoBlockItemReadableName(type)
          )
        ),
      ];

      if (extraProps?.stage && stage?.title) {
        return [stage.title, ...initBreadcrumb];
      }

      if (venue?.title) {
        return [venue?.title, ...initBreadcrumb];
      }
    },
    [infoBlockType, stage?.title, venue?.title, extraProps?.stage]
  );

  if (!infoBlockType || !infoBlock) return null;

  const handleEdit = () => {
    pushRoute(editPath, { params: { infoBlockSlug: infoBlockType.type } });
  };

  const handleDelete = () => {
    if (infoBlock) {
      openDeleteInfoBlockModal({
        ...infoBlock,
        ...extraProps,
      });
    }
  };

  return (
    <Card
      id={infoBlockType.type}
      icon={icon}
      title={`${infoBlockType.parentLabel}: ${infoBlockType.label}`}
      actions={getInfoBlockActions({
        userPermissions,
        handleEdit,
        handleDelete,
        openSuggestEditModal: () =>
          extraProps?.stage
            ? openSuggestStageEditModal({
                type: "StageInfoBlock",
                infoBlockSlug: infoBlockType.type,
              })
            : openSuggestEditModal({
                type: "VenueInfoBlock",
                infoBlockSlug: infoBlockType.type,
              }),
      })}
      accentColor={brandColor}
      actionDataTestId={`${infoBlock.type}_action_select`}
    >
      <Spacer size="l">
        <RTEContent content={infoBlock.text} />
        {itemsProps.map(
          ({ items, type, ...rest }) =>
            !!items?.length && (
              <InfoBlockItems
                breadcrumbs={getBreadcrumbs(type)}
                items={items}
                key={`${infoBlock.id}_${type}`}
                type={type}
                {...rest}
              />
            )
        )}
      </Spacer>
    </Card>
  );
};
